<template>
  <div class="checkout-items">
    <!-- loaded -->
    <template v-if="isLoaded">
      <b-modal
        id="buyingsummary"
        ref="buyingsummary"
        hide-header
        hide-header-close
        hide-footer
        size="xl"
        centered
      >
        <div class="pb-1 border-bottom">
          <span class="custom-modal-heading">Buying Summary</span>
          <feather-icon
            class="float-right close-x-icon"
            icon="XIcon"
            size="24"
            @click="closeModal"
          />
        </div>
        <buying-summary
          :cart-products="originalProducts"
          @closeModal="closeModal"
        />
      </b-modal>
      <!-- has product -->
      <template v-if="productsSlice.length || originalProducts.length">
        <b-row
          class="sticky-input"
          :class="isPopup ? 'sticky-input-popup' : ''"
        >
          <b-col class="col-11 col-md-6 col-lg-5">
            <b-form-input
              v-model="searchText"
              placeholder="Search by products, style code..."
            />
            <feather-icon
              icon="SearchIcon"
              size="18"
              class="cart-search-icon"
            />
          </b-col>
          <b-col
            v-if="!isPopup"
            class="col-12 col-md-6 col-lg-7 d-flex justify-content-end"
          >
            <b-button
              v-b-modal.clear-cart
              class="mr-1"
              variant="outline-danger clear-cart-btn"
            >
              <shopping-bag-deletete-icon class="align-bottom" />
              Clear Cart
            </b-button>
            <b-button
              variant="outline-secondary-black"
              class="mr-1"
              @click="saveCartProducts({ shouldSaveCart: true })"
            >
              Save Cart
            </b-button>
          </b-col>
        </b-row>
        <!-- Cart errors -->
        <cart-errors
          v-if="isCartHasErrors"
          :key="isCartHasErrors"
          :cart-id="$store.getters.getCart._id"
          :cart-products="products"
          @update:remove-products-brand-cart="removeMultipleProducts"
          @on-close-cart-error-popup="(error) => trackErrorPopupClicks(error, 'close')"
          @on-click-cart-error-button="(error) => trackErrorPopupClicks(error, 'btn-click')"
        />
        <div
          v-for="product in productsSlice"
          v-show="isProductMatch(product)"
          :key="product._id"
          class="mb-1 cart-product"
          :class="{
            'has-error': isNotValidMinQty(product, product.lineTotalQuantity) || isSegmentQuantityBlocked(product, product.lineTotalQuantity),
            'is-product-not-valid':
              isCartHasErrors && getCartProductsErrorStatusText(product),
            'zero-qty-product': isCartHasErrors && isProductHasZeroQty(product),
            'decimal-qty-product': isCartHasErrors && isProductWithDecimalQty(product),
          }"
        >
          <!-- cart error text -->
          <cart-product-error-text
            :error-text="getCartProductsErrorStatusText(product)"
          />
          <b-row
            class="mx-0"
            no-body
          >
            <!-- Product Image -->
            <b-col class="item-img col-md-3 col-xl-2 pl-0 col-12">
              <b-link
                :to="{
                  name: 'product-detail',
                  params: {
                    id: product._id,
                    collectionId: product.collectionId,
                  },
                }"
              >
                <b-img-lazy
                  class="imgcss"
                  :src="parseProductImage(product)"
                  :alt="`${product.name}-${product._id}`"
                  @error.native="imageLoadError"
                />
              </b-link>
            </b-col>

            <!-- Product Details: Card Body -->
            <b-col
              class="
                col-md-7 col-xl-8
                border-right
                pt-2
                d-flex
                flex-column
                col-12
              "
            >
              <div class="item-name d-flex justify-content-between">
                <div>
                  <b-link
                    class="product-link"
                    :to="{
                      name: 'product-detail',
                      params: {
                        id: product._id,
                        collectionId: product.collectionId,
                      },
                    }"
                  >
                    {{ product.name }}
                  </b-link>
                  <b-link class="company-name">
                    <b-breadcrumb
                      class="breadcrumb-products mb-0 pl-0"
                      :items="[
                        product.department,
                        product.category,
                        product.subCategory,
                      ]"
                    />
                  </b-link>
                  <div class="cart-price-title">
                    <div class="mt-75">
                      Style Code:
                      <span class="cart-price-value">{{
                        product.styleCode
                      }}</span>
                    </div>
                    <!-- KP-4350: tags from the retailer's segment (e.g. MCA) -->
                    <v2-segment-tags
                      :product="product"
                      inline
                      class="mt-25 mb-25"
                    />
                    <div>
                      Retail Price:
                      <span class="cart-price-value">{{
                        formatCurrency(product.retailPrice, product.currency)
                      }}</span>
                    </div>
                    <div>
                      Wholesale Selling Price:
                      <span
                        v-if="!product.discountedPrice"
                        class="cart-price-value"
                        :class="{
                          'text-line-through color-red':
                            !!product.discountedPrice,
                        }"
                      >
                        {{ formatCurrency(product.price, product.currency) }}
                      </span>
                      <span
                        v-if="product.discountedPrice"
                        class="cart-price-value discounted-price"
                      >
                        {{
                          formatCurrency(
                            product.discountedPrice,
                            product.currency
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="text-right">
                  <div
                    v-if="collectionEndDate"
                    class="font-weight-normal mt-50"
                  >
                    <span class="mb-2 cart-price-title">
                      Order Deadline:
                      <span
                        class="cart-price-value"
                      >{{ formatDate(collectionEndDate) }}
                      </span>
                    </span>
                  </div>
                  <div class="font-weight-normal mt-50">
                    <span
                      v-if="
                        product.shippingStartDate && product.shippingEndDate
                      "
                      class="mb-2 cart-price-title"
                    >
                      Availability Date:
                      <span
                        class="cart-price-value"
                      >{{ formattedDate(product.shippingStartDate) }} to
                        {{ formattedDate(product.shippingEndDate) }}</span>
                    </span>
                  </div>
                  <div
                    v-if="product.isCarton"
                    class="item-quantity mt-1 mr-1"
                  >
                    <span class="cart-price-title">Qty:</span>
                    <b-form-spinbutton
                      v-model="product.quantity"
                      size="sm"
                      class="ml-75"
                      inline
                      :max="getMaxQuantity(product.quantityAvailable)"
                      :disabled="
                        isProductValueDisabled(product.quantityAvailable)
                      "
                      min="1"
                      @input="updateQuantity(product)"
                    />
                    <div
                      v-if="hasProductMaxQuantity(product.quantityAvailable)"
                      class="
                        text-nowrap text-right
                        color-actions-text-grey
                        mt-2-px
                        mr-1
                      "
                    >
                      Max Qty: {{ product.quantityAvailable }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- variants -->
              <product-variants
                :is-deleted="product.isDeleted"
                :is-carton="product.isCarton"
                :items="product.items"
                :is-edit-enabled="true"
                :min-qty-per-size="productMinQtyPerSize(product)"
                @update-variants="
                  (variants) => updateProductVariants(variants, product)
                "
              />
            </b-col>

            <!-- Product Options/Actions -->
            <b-col
              class="
                item-options
                text-center
                d-flex
                flex-column flex-md-column
                md-0
                ml-md-auto
                col-md-2 col-xl-2
                align-items-center
                mt-2
                col-12
              "
            >
              <div class="product-actions">
                <span
                  v-if="$route.name === 'checkout'"
                  v-b-tooltip.hover.bottom.v-primary
                  class="mr-1"
                  title="Replace Product"
                  @click="handleReplaceProduct(product)"
                >
                  <recycle-icon />
                </span>
                <span
                  v-b-modal="`modal-remove-product-${product._id}`"
                  v-b-tooltip.hover.bottom.v-primary
                  class=""
                  title="Delete Product from Cart"
                  @click="trackRemoveProduct(product._id, 'remove_button_click')"
                >
                  <delete-icon />
                </span>
              </div>

              <b-modal
                :id="`modal-remove-product-${product._id}`"
                ok-variant="danger"
                ok-title="Remove product"
                cancel-title="Dismiss"
                modal-class="modal-danger"
                centered
                title="Remove this product?"
                @ok="confirmProductRemoval(product)"
                @hide="trackRemoveProduct(product._id, 'close_modal')"
              >
                <b-card-text>
                  Are you sure you want to delete this product?
                </b-card-text>
              </b-modal>

              <div class="item-wrapper my-2 my-md-auto ml-auto ml-md-0">
                <div class="item-cost text-center d-flex d-md-block">
                  <h4
                    v-if="variantLoaded"
                    class="
                      item-price
                      mr-50 mr-md-0
                      font-weight-bolder
                      color-neutral-black
                    "
                  >
                    Total Value:
                    {{
                      formatCurrency(
                        product.lineTotalPriceWithDiscount ||
                          product.lineTotalPrice ||
                          0,
                        product.currency
                      )
                    }}
                  </h4>
                  <b-card-text
                    v-if="variantLoaded"
                    class="mt-2"
                  >
                    Total Units:
                    {{ formatNumber(product.lineTotalQuantity || 0) }}
                  </b-card-text>
                  <p class="text-primary">
                    {{ minQtyErrorMessage(product) }}
                  </p>
                  <p
                    v-if="segmentMaxQtyLabel(product)"
                    :class="isSegmentQuantityBlocked(product, product.lineTotalQuantity) ? 'text-danger' : 'text-primary'"
                  >
                    {{ segmentMaxQtyLabel(product) }}
                  </p>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </template>
    </template>
    <!-- loading -->
    <template v-else>
      <b-card class="text-center p-4">
        <b-spinner />
      </b-card>
    </template>
  </div>
</template>

<script>
import DeleteIcon from '@/@core/assets/svg-components/DeleteIcon.vue'
import RecycleIcon from '@/@core/assets/svg-components/RecycleIcon.vue'
import ShoppingBagDeleteteIcon from '@/@core/assets/svg-components/ShoppingBagDeleteteIcon.vue'
import analytics from '@/@core/utils/analytics'
import { formatDate } from '@/@core/utils/filter'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import {
  formatNumber,
  formatObject,
  getMaxQuantity,
  hasProductMaxQuantity,
  imageLoadError,
  isProductValueDisabled,
  searchProductUsingFields,
} from '@/@core/utils/utils'
import constants, {
  CART_PRODUCTS_SEARCH_FIELDS,
  CART_PRODUCTS_SEARCH_ITEMS_FIELDS,
  PRODUCTS_LIMIT,
  marketplaceAccessDeniedMessage,
} from '@/constants'
import {
  buildCartSearchIndex,
  CART_SEARCH_INDEX_THRESHOLD,
} from '@/@core/utils/cart-search-index'
import store from '@/store'
import {
  FETCH_CART_COUNT,
  REMOVE_PRODUCT_FROM_CART,
  SET_REPLACE_CART_PRODUCT,
  UPDATE_CART_ITEMS_COUNT,
} from '@/store/modules/shop.module'
import ProductVariants from '@/views/apps/product/detail/ProductVariants.vue'
import {
  constants as c,
  utils,
  productUtil,
} from '@kingpin-global/kingpin-utils-frontend'

import { onMounted, onUnmounted, watch } from '@vue/composition-api'
import {
  BBreadcrumb,
  BButton,
  BCard,
  BCardText,
  BCol,
  BFormInput,
  BFormSpinbutton,
  BImgLazy,
  BLink,
  BModal,
  BRow,
  BSpinner,
  VBModal,
  VBTooltip,
} from 'bootstrap-vue'
import { mapState } from 'vuex'
import {
  LOAD_CARTS,
  UPDATE_CART_ERRORS,
} from '@/store/modules/checkout-v2.module'
import CartProductErrorText from '@/views/components/cart-products-error/CartProductErrorText.vue'
import CartErrorsMixinVue from '@/views/components/cart-products-error/CartErrorsMixin.vue'
import V2SegmentTags from '@/components/v2/V2SegmentTags.vue'
import UserRoleMixinVue from '../UserRoleMixin.vue'
import { useEcommerce, useEcommerceUi } from '../useEcommerce'
import BuyingSummary from './BuyingSummary.vue'
import {
  seedAndSnapshotCartProducts,
  getCartProductsErrorStatusText,
  getSortedCartProducts,
  initializeData,
  isNotValidMinQty,
  isProductHasZeroQty,
  isProductWithDecimalQty,
  minQtyErrorMessage,
  parseProductItems,
  processProductVariants,
  isSegmentQuantityBlocked,
  productMinQtyPerSize,
  segmentMaxQtyLabel,
  segmentMaxQtyMessage,
  isSegmentCannotOrder,
} from './cart-products-utils'
import CartErrors from '../../components/cart-products-error/CartErrors.vue'
import { useCartProductTrackers } from './trackHelpers/useCartProductsTrackers'

const { getProductAndCartTotals } = productUtil

const { formatCurrency, formattedDate } = utils

const { BRAND_ACCESS } = c

// --- Module-level helper functions (extracted from setupFunctions) ---

function getScrollContainer(modalId) {
  const modalContent = document
    .getElementById(modalId)
    ?.querySelector('.modal-body')
  return modalContent || window
}

function getScrollPosition(scrollContainer) {
  return scrollContainer === window
    ? window.scrollY
    : scrollContainer.scrollTop
}

function getScrollHeight(scrollContainer) {
  return scrollContainer === window
    ? document.body.scrollHeight
    : scrollContainer.scrollHeight
}

function getClientHeight(scrollContainer) {
  return scrollContainer === window
    ? window.innerHeight
    : scrollContainer.clientHeight
}

function applyProductItems(product, items) {
  const prevItems = product.items
  const shouldReplace = items !== prevItems
    && (!prevItems || prevItems.length !== items.length
      || items.some((item, idx) => item !== prevItems[idx]))
  if (shouldReplace) {
    product.items = items
  }
}

function syncOriginalProductItems(originalProducts, product, items) {
  const index = originalProducts.value.findIndex(
    p => p._id === product._id,
  )
  originalProducts.value[index].items = items

  if (!product.isCarton) {
    originalProducts.value[index].hasError = product?.hasError || false
  }
}

function handleReplacingProduct(root, removeProductFromCart) {
  const replacingProduct = root.$store.getters.getReplaceCarProduct
  if (!replacingProduct) return
  removeProductFromCart(replacingProduct)
  root.$store.commit(SET_REPLACE_CART_PRODUCT, null)
}

function setCartMetadataAndTrack(resData, cartProducts, collectionName, collectionId) {
  collectionName.value = resData?.collectionName
  collectionId.value = resData?.collectionId
  if (!cartProducts) return
  analytics.track(
    constants.TRACKS.PAGES.RETAILER_OPENS_BASKET,
    formatObject({
      collectionName: collectionName.value,
      noOfProductsInCart: resData?.products?.length || 0,
    }),
  )
}

function collectSizeValidationMessages(prod, messages) {
  prod.variants.forEach(item => {
    const hasSizeViolation = Object.keys(item)
      .filter(k => k !== 'color')
      .some(k => item[k].value && item[k].value < prod.minQtyPerSize)
    if (hasSizeViolation) {
      messages.push(
        `Minimum quantity per size for "${prod.name}" is ${prod.minQtyPerSize}`,
      )
    }
  })
}

function collectStyleQtyMessage(prod) {
  if (prod?.minQtyPerStyle && prod.lineTotalQuantity < prod?.minQtyPerStyle) {
    return `Minimum quantity per style for "${prod.name}" is ${prod.minQtyPerStyle}`
  }
  return null
}

function collectProductValidationMessages(prod, messages) {
  // KP-4605: a style whose minimum is above its segment maximum gets one message
  if (isSegmentCannotOrder(prod)) {
    messages.push(segmentMaxQtyMessage(prod))
    return
  }
  if (!prod?.isCarton && prod?.minQtyPerSize) {
    collectSizeValidationMessages(prod, messages)
  }
  const styleMsg = collectStyleQtyMessage(prod)
  if (styleMsg) {
    messages.push(styleMsg)
  }
  // KP-4605: the segment maximum is an extra limit
  if (isSegmentQuantityBlocked(prod, prod.lineTotalQuantity)) {
    messages.push(segmentMaxQtyMessage(prod))
  }
}

function collectMinQtyValidationMessages(products) {
  const messages = []
  products.value.forEach(prod => {
    collectProductValidationMessages(prod, messages)
  })
  return messages
}

function buildProductPayloadEntry(prod, originalProduct, payload, isBrand) {
  if (prod.isCarton) {
    if (originalProduct?.quantity !== prod.quantity || isBrand.value) {
      payload.products.push({ productId: prod._id, quantity: prod.quantity })
    }
    return
  }

  if (Array.isArray(prod.variants) && prod.variants.length > 0) {
    processProductVariants(prod, originalProduct, payload, { isBrand })
    return
  }

  payload.products.push({
    productId: prod._id,
    items: prod.items.map(item => ({ sku: item.sku, value: item.value })),
  })
}

function buildSaveCartPayload(products, rawProducts, props, isBrand) {
  const payload = { products: [] }

  if (props.isPopup && props.retailerId) {
    payload.retailerId = props.retailerId
  }

  products.value.forEach(prod => {
    const originalProduct = rawProducts.value.find(p => p._id === prod._id)
    buildProductPayloadEntry(prod, originalProduct, payload, isBrand)
  })

  return payload
}

async function removeProductFromCartApi({
  emit, products, REMOVE_PRODUCT_FROM_CART_MSG,
  removeProductFromProductList, updateTotalPriceAndUnits,
  trackRemoveProduct, product,
}) {
  try {
    await store.dispatch(REMOVE_PRODUCT_FROM_CART, { productId: product._id })
    trackRemoveProduct(product._id, 'product_remove_success')

    apiToastSuccess(REMOVE_PRODUCT_FROM_CART_MSG)
    store.commit(UPDATE_CART_ITEMS_COUNT, store.state.shop.cartItemsCount - 1)
    await store.dispatch(FETCH_CART_COUNT)
    removeProductFromProductList(product)

    if (products.value.length === 0) {
      emit('has-cart-product', false)
    } else {
      updateTotalPriceAndUnits()
    }

    analytics.track(constants.TRACKS.ACTIONS.REMOVE_FROM_CART, formatObject(product))
    store.commit(UPDATE_CART_ERRORS)
  } catch (err) {
    trackRemoveProduct(product._id, 'product_remove_failed', err)
  }
}

function handleSaveCartSuccess({
  res, props, isBrand, collectionName, payload,
  addProductInCart, notesPayload, shouldSaveCart,
  rawProducts, originalProducts,
}) {
  if (!props.isPopup && !shouldSaveCart) {
    apiToastSuccess(res.data.message || 'Saved cart!')
  }
  if (!isBrand.value) {
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_SAVES_CART,
      formatObject({
        noOfProductsInCart: payload.products?.length,
        collectionName: collectionName.value,
      }),
    )
  }
  if (notesPayload?.notes) {
    addProductInCart(notesPayload)
  }
  rawProducts.value = JSON.parse(JSON.stringify(originalProducts.value))
}

function handleSaveCartError({
 err, isCartExport, shouldSaveCart, root,
}) {
  if (err?.response?.data?.data?.hasRetailerCart) {
    isCartExport.value = shouldSaveCart
    root.$bvModal.show('clear-retailer-cart')
  }
}

function executeSaveCartRequest({
  payload, params, props, isBrand, collectionName,
  isCartExport, root, addProductInCart,
  rawProducts, originalProducts, notesPayload, shouldSaveCart,
  resolve, reject,
}) {
  addProductInCart(payload, params)
    .then(res => {
      handleSaveCartSuccess({
        res,
props,
isBrand,
collectionName,
payload,
        addProductInCart,
notesPayload,
shouldSaveCart,
        rawProducts,
originalProducts,
      })
      resolve()
    })
    .catch(err => {
      handleSaveCartError({
 err, isCartExport, shouldSaveCart, root,
})
      reject(new Error('Failed to save cart'))
    })
}

export default {
  name: 'CartProducts',
  components: {
    V2SegmentTags,
    BCard,
    BCardText,
    BLink,
    BImgLazy,
    BButton,
    BBreadcrumb,
    BSpinner,
    BRow,
    BCol,
    BFormInput,
    ProductVariants,
    BuyingSummary,
    BModal,
    RecycleIcon,
    DeleteIcon,
    ShoppingBagDeleteteIcon,
    BFormSpinbutton,
    CartErrors,
    CartProductErrorText,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  mixins: [UserRoleMixinVue, CartErrorsMixinVue],
  props: {
    updateDisablePlaceOrder: {
      type: Function,
      default: () => {},
    },
    cartProducts: {
      type: Array,
      default: () => [],
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    retailerId: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imageLoadError,
      formatDate,
      getMaxQuantity,
      isProductValueDisabled,
      hasProductMaxQuantity,
      marketplaceAccessDeniedMessage,
      isNotValidMinQty,
      minQtyErrorMessage,
      isSegmentQuantityBlocked,
      productMinQtyPerSize,
      segmentMaxQtyLabel,
      getCartProductsErrorStatusText,
      isProductHasZeroQty,
      isProductWithDecimalQty,
    }
  },
  computed: {
    ...mapState({
      profile: state => state.auth.profileData,
    }),
    isEnabledMarketplace() {
      return this.profile?.access?.includes(BRAND_ACCESS.MARKETPLACE)
    },
  },
  methods: {
    closeModal() {
      this.$refs.buyingsummary.hide()
    },
  },
  emits: [
    'update-total',
    'update-total-units',
    'has-cart-product',
    'remove-product',
    'remove-color',
    'ship-now',
    'remove-product-selected-from-brand',
    'update-notes',
    'remove-multiple-products',
  ],

  setup(props, { emit, root }) {
    let productsLimit = PRODUCTS_LIMIT
    let [productsSliceX, productsSliceY, pageNumber] = [0, 0, 1]
    let totals = {
      totalPrice: 0,
      totalQuantity: 0,
      totalPriceWithDiscount: 0,
    }
    // Lowercased searchable-token strings keyed by product._id. Only populated
    // for carts that cross CART_SEARCH_INDEX_THRESHOLD so small carts still
    // use the original matcher and pay no build cost.
    let searchIndex = null

    const {
      variantLoaded,
      isLoaded,
      products,
      productsSlice,
      searchText,
      originalProducts,
      rawProducts,
      collectionEndDate,
      discountInCredit,
      collectionName,
      collectionId,
      isBrand,
      isCartExport,
    } = initializeData(root, props)

    const { debouncedTrackSearchProductsInput, trackErrorPopupClicks, trackRemoveProduct } = useCartProductTrackers({
      isBrand,
      collectionId,
      props,
      products,
      cartId: root.$store.getters.getCart._id,
      userId: root.$store.getters.getCart.userId,
    })

    const { addProductInCart, REMOVE_PRODUCT_FROM_CART_MSG } = useEcommerce()
    let isCartModified = false
    const cartModifiedState = {
      get: () => isCartModified,
      set: val => { isCartModified = val },
    }

    const paginationState = {
      getSliceX: () => productsSliceX,
      setSliceX: val => { productsSliceX = val },
      getSliceY: () => productsSliceY,
      setSliceY: val => { productsSliceY = val },
      setPageNumber: val => { pageNumber = val },
    }

    // --- Totals ---
    const updateTotalPriceAndUnits = () => {
      const currency = products.value[0].currency
      totals = getProductAndCartTotals(
        products.value,
        products.value[0].discount,
      )
      if (props.isPopup) {
        productsSlice.value = []
        products.value.slice(0, productsSliceY).forEach(p => {
          productsSlice.value.push(p)
        })
      }
      emit(
        'update-total',
        totals.totalPriceWithDiscount || totals.totalPrice || 0,
        currency,
      )
      emit('update-total-units', totals.totalQuantity)
    }

    // --- Product list removal ---
    const loadCartHasNoProducts = () => {
      if (!products.value?.length && !isBrand.value) {
        root.$store.dispatch(LOAD_CARTS)
      }
    }

    const removeProductFromProductList = product => {
      const filterOutProduct = prod => prod._id !== product._id
      products.value = products.value.filter(filterOutProduct)
      originalProducts.value = originalProducts.value.filter(filterOutProduct)
      productsSlice.value = productsSlice.value.filter(filterOutProduct)
      if (searchIndex && product?._id) {
        delete searchIndex[product._id]
      }
      loadCartHasNoProducts()
    }

    // --- Cart removal (API call) ---
    const removeProductFromCart = product => removeProductFromCartApi({
      emit,
products,
REMOVE_PRODUCT_FROM_CART_MSG,
      removeProductFromProductList,
updateTotalPriceAndUnits,
      trackRemoveProduct,
product,
    })

    // --- Single / multiple product removal ---
    const removeProduct = product => {
      if (!props.isPopup) {
        removeProductFromCart(product)
        return
      }
      emit('remove-product-selected-from-brand', product)
      removeProductFromProductList(product)
      updateTotalPriceAndUnits()
      apiToastSuccess('Product removed successfully')
    }

    const removeMultipleProducts = removableBrandCartProductIds => {
      removableBrandCartProductIds.forEach(productId => {
        const product = products.value.find(p => p._id === productId)
        if (product) {
          removeProductFromProductList(product)
        }
      })
      if (products.value.length > 0) {
        updateTotalPriceAndUnits()
      }
      emit('remove-multiple-products', removableBrandCartProductIds)
      apiToastSuccess('Products removed successfully')
    }

    // --- Replace product ---
    const onClickReplace = async product => {
      root.$store.commit(SET_REPLACE_CART_PRODUCT, product)
      await root.$router.push({
        name: 'collection/products',
        params: { collectionId: product.collectionId },
      })
    }

    // --- Fetch cart products ---
    const fetchCartProducts = () => {
      const resData = root.$store.getters.getCart
      let hasProducts = false
      emit('update-notes', resData?.notes || '')
      const cartProducts = resData?.products || []

      if (cartProducts.length) {
        const { original, raw } = seedAndSnapshotCartProducts(cartProducts)
        originalProducts.value = original
        rawProducts.value = raw

        products.value = cartProducts
        updateTotalPriceAndUnits()
        const sliceEnd = Math.min(products.value.length, productsLimit)
        paginationState.setSliceY(sliceEnd)

        products.value
          .slice(0, sliceEnd)
          .forEach(p => productsSlice.value.push(p))

        paginationState.setSliceX(sliceEnd)
        hasProducts = true
      }

      if (resData.hasOwnProperty('collectionEndDate')) {
        collectionEndDate.value = resData.collectionEndDate
      }
      if (resData.hasOwnProperty('discountInCredit')) {
        discountInCredit.value = resData.discountInCredit
      }
      isLoaded.value = true

      emit('has-cart-product', hasProducts)
      handleReplacingProduct(root, removeProductFromCart)
      setCartMetadataAndTrack(resData, cartProducts, collectionName, collectionId)
    }

    // --- Variant updates ---
    const updateProductVariants = (variants, product) => {
      if (isBrand.value) {
        analytics.track(
          constants.TRACKS.ACTIONS.BRAND_UPDATES_PRODUCT_IN_CART,
          formatObject(product),
        )
      }
      cartModifiedState.set(true)
      product.variants = variants
      const items = parseProductItems(product)
      applyProductItems(product, items)
      syncOriginalProductItems(originalProducts, product, items)

      updateTotalPriceAndUnits()
      variantLoaded.value = true
    }

    // --- Quantity updates ---
    const updateQuantity = product => {
      if (isBrand.value) {
        analytics.track(
          constants.TRACKS.ACTIONS.BRAND_UPDATES_PRODUCT_IN_CART,
          formatObject(product),
        )
      }
      cartModifiedState.set(true)
      const index = originalProducts.value.findIndex(
        p => p._id === product._id,
      )
      originalProducts.value[index].quantity = product.quantity
      updateTotalPriceAndUnits()
    }

    // --- Save cart ---
    const saveCartProducts = async ({
      notesPayload = {},
      shouldSaveCart = false,
      isExport = false,
      forceSave = false,
    } = {}) => new Promise((resolve, reject) => {
        const isCartUnchanged = !forceSave && !shouldSaveCart && !cartModifiedState.get() && !props.isPopup
        if (isCartUnchanged) {
          resolve()
          return
        }

        const skipMinQtyValidation = shouldSaveCart || isExport
        const minQtyValidationMessages = collectMinQtyValidationMessages(products)
        if (!skipMinQtyValidation && minQtyValidationMessages.length > 0) {
          minQtyValidationMessages.forEach(message => { apiToastWarning(message) })
          reject(new Error('Minimum quantity validation failed'))
          return
        }

        cartModifiedState.set(false)
        const payload = buildSaveCartPayload(products, rawProducts, props, isBrand)
        const params = {
          isUpdateFromCartPage: true,
          ...(skipMinQtyValidation && { skipMinQtyValidation: true }),
        }

        if (!forceSave && !payload?.products?.length && !isBrand.value) {
          resolve()
          return
        }

        executeSaveCartRequest({
          payload,
params,
props,
isBrand,
collectionName,
          isCartExport,
root,
addProductInCart,
          rawProducts,
originalProducts,
notesPayload,
shouldSaveCart: isExport,
          resolve,
reject,
        })
      })

    // --- Search ---
    const searchFields = CART_PRODUCTS_SEARCH_FIELDS
    const itemFields = CART_PRODUCTS_SEARCH_ITEMS_FIELDS

    // Tests whether a single product matches the current search query. Used
    // from the template via `v-show` so search hides non-matching cards via
    // CSS instead of unmounting them. Filtering a large cart down to a few
    // matches previously unmounted dozens of <b-table>/<b-form-input>
    // components synchronously, which on iframe pages triggered Chrome's
    // "Page Unresponsive" dialog.
    const isProductMatch = product => {
      const query = searchText.value?.trim()?.toLowerCase() || ''
      if (!query) return true
      if (searchIndex) {
        const tokens = searchIndex[product?._id]
        return tokens ? tokens.indexOf(query) !== -1 : false
      }
      return searchProductUsingFields(
        product, searchFields, searchText.value, itemFields,
      )
    }

    // --- Initialize with cart products ---
    const initializeWithCartProducts = (cartProducts, isErrorProducts = false) => {
      let hasProducts = false
      productsLimit = 20

      if (cartProducts?.length) {
        const { original, raw } = seedAndSnapshotCartProducts(cartProducts)
        originalProducts.value = original
        rawProducts.value = raw

        // Build the pre-lowercased search index only for carts large enough
        // to make per-keystroke field-walking expensive.
        searchIndex = cartProducts.length >= CART_SEARCH_INDEX_THRESHOLD
          ? buildCartSearchIndex(originalProducts.value)
          : null

        if (isErrorProducts) {
          products.value = getSortedCartProducts(cartProducts)
          productsSlice.value = []
          productsSliceY = 0
        } else {
          products.value = cartProducts
        }

        updateTotalPriceAndUnits()

        productsSliceY = Math.min(products.value.length, productsLimit)

        products.value
          .slice(0, productsSliceY)
          .forEach(p => productsSlice.value.push(p))

        productsSliceX = productsSliceY

        hasProducts = true
      }

      emit('has-cart-product', hasProducts)
      isLoaded.value = true
    }

    // --- Scroll handling ---
    const onScrollLoader = scrollContainer => {
      const scrollPosition = getScrollPosition(scrollContainer)
      const scrollHeightVal = getScrollHeight(scrollContainer)
      const clientHeightVal = getClientHeight(scrollContainer)

      if (scrollPosition + clientHeightVal < scrollHeightVal - 2000) return
      if (products.value.length <= productsSliceX) return

      pageNumber += 1

      productsSliceY = Math.min(
        products.value.length,
        pageNumber * productsLimit,
      )

      products.value
        .slice(productsSliceX, productsSliceY)
        .forEach(p => productsSlice.value.push(p))

      productsSliceX = productsSliceY
    }

    const onScrollHandler = () => {
      const scrollContainer = getScrollContainer(props.modalId)
      onScrollLoader(scrollContainer)
    }

    // --- UI helpers ---
    const handleReplaceProduct = product => {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_REPLACE_PRODUCT_BUTTON, {
          cartId: root.$store.getters.getCart._id,
          retailerId: props.retailerId || root.$store.getters.getCart.userId || '',
          productId: product._id,
        })
      onClickReplace(product)
    }

    const confirmProductRemoval = product => {
      trackRemoveProduct(product._id, 'confirm_modal_button_click')
      removeProduct(product)
      if (isBrand.value) {
        trackRemoveProduct(product._id, 'product_remove_success')
      }
    }

    // --- Watchers ---
    watch(searchText, val => {
      const trackName = !isBrand.value
      ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SEARCHES_FOR_PRODUCT
      : constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_SEARCHES_FOR_PRODUCT
      debouncedTrackSearchProductsInput(trackName, val, collectionId.value, props.retailerId, isBrand.value, root.$store.getters.getCart)
    })

    // --- Lifecycle ---
    onMounted(() => {
      const scrollContainer = getScrollContainer(props.modalId)
      scrollContainer.addEventListener('scroll', onScrollHandler)
    })

    onUnmounted(() => {
      const scrollContainer = getScrollContainer(props.modalId)
      scrollContainer.removeEventListener('scroll', onScrollHandler)
    })

    if (props.cartProducts.length > 0) {
      initializeWithCartProducts(props.cartProducts)
    } else {
      fetchCartProducts()
    }

    const { parseProductImage } = useEcommerceUi()

    return {
      products,
      productsSlice,
      isLoaded,
      variantLoaded,
      formattedDate,
      searchText,
      isProductMatch,
      originalProducts,
      collectionEndDate,
      discountInCredit,
      collectionId,
      isCartExport,
      trackErrorPopupClicks,
      // UI
      trackRemoveProduct,
      handleReplaceProduct,
      confirmProductRemoval,
      onClickReplace,
      parseProductImage,
      updateProductVariants,
      saveCartProducts,
      fetchCartProducts,
      removeMultipleProducts,

      // Filter
      formatNumber,
      formatCurrency,
      updateQuantity,
      initializeWithCartProducts,
    }
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-ecommerce.scss';
@import '~@core/scss/base/pages/app-cart-products.scss';
</style>
